import { Currency } from '@sequencehq/api'
import {
  Box,
  Card,
  ControlEmptyState,
  EmptyState,
  Flex,
  GreyGrey10,
  GreyGrey70,
  IconBank,
  InputSelectControlField,
  Primary13Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  Skeleton,
  Text
} from '@sequencehq/core-components'
import { FC } from 'react'
import { useEnabledCurrencies } from 'SettingsV2/index'
import {
  PaymentBankAccountOption,
  PaymentBankAccountsByCurrency
} from 'Integrations/integrationsConfig/common/PaymentBankAccounts/paymentBankAccounts.types'

type Props = {
  /** The accounting service payments are pushed to, e.g. 'QuickBooks'. */
  serviceName: string
  fallbackDescription?: string
  allowClearing?: boolean
  bankAccounts: PaymentBankAccountOption[]
  value: PaymentBankAccountsByCurrency
  onChange: (value: PaymentBankAccountsByCurrency) => void
  'data-testid': string
}

export const PaymentBankAccountsCard: FC<Props> = ({
  serviceName,
  fallbackDescription,
  allowClearing,
  bankAccounts,
  value,
  onChange,
  'data-testid': dataTestId
}) => {
  const currencies = useEnabledCurrencies()
  const hasNoEnabledCurrencies = currencies.status === 'ERROR'

  return (
    <Card>
      <Flex data-testid={dataTestId} gap={4} flexDirection="column">
        <Flex gap={1} flexDirection="column">
          <Text {...Primary14SemiBold}>Payments</Text>
          <Text {...Primary13Regular} color={GreyGrey70}>
            {fallbackDescription ??
              `Set an account for pushing payments in each currency. Leave it empty and ${serviceName} places it in Undeposited Funds in your home currency.`}
          </Text>
        </Flex>

        <Flex
          backgroundColor={GreyGrey10}
          borderRadius={6}
          alignItems="center"
          alignContent="center"
          width="100%"
          p={4}
          gap={2}
        >
          <Flex gap={4} flexDirection="column" width="100%">
            {!hasNoEnabledCurrencies && (
              <Text
                data-testid={`${dataTestId}.accountsTitle`}
                {...Primary13SemiBold}
              >
                Accounts
              </Text>
            )}
            <Flex gap={2} flexDirection="column">
              <BankAccountRows
                currencies={currencies}
                serviceName={serviceName}
                allowClearing={allowClearing}
                bankAccounts={bankAccounts}
                value={value}
                onChange={onChange}
                data-testid={dataTestId}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}

const BankAccountRows: FC<
  Props & { currencies: ReturnType<typeof useEnabledCurrencies> }
> = ({
  currencies,
  serviceName,
  allowClearing,
  bankAccounts,
  value,
  onChange,
  'data-testid': dataTestId
}) => {
  if (currencies.status === 'LOADING') {
    return <Skeleton width="100%" height="40px" />
  }

  // The hook reports an account with no enabled currencies as an error too.
  if (currencies.status === 'ERROR') {
    return (
      <EmptyState
        data-testid={`${dataTestId}.emptyState`}
        icon={IconBank}
        iconSize="sm"
        title="No currencies enabled"
        body="Enable a currency for this account before configuring payment accounts."
        padding="16px 0"
        minHeight="initial"
      />
    )
  }

  return (
    <>
      {currencies.enabledCurrencies.map(currency => (
        <BankAccountRow
          key={currency}
          currency={currency}
          serviceName={serviceName}
          allowClearing={allowClearing}
          bankAccounts={bankAccounts}
          value={value}
          onChange={onChange}
          data-testid={dataTestId}
        />
      ))}
    </>
  )
}

const BankAccountRow: FC<Props & { currency: Currency }> = ({
  currency,
  serviceName,
  allowClearing,
  bankAccounts,
  value,
  onChange,
  'data-testid': dataTestId
}) => {
  const options = optionsForCurrency(bankAccounts, currency)
  const hasAccountsInCurrency = options.length > 0
  const canClear =
    allowClearing && (hasAccountsInCurrency || Boolean(value[currency]))

  return (
    <Flex
      backgroundColor={GreyGrey10}
      borderRadius={6}
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      py={1}
      pr={1}
      gap={4}
    >
      <Text {...Primary13Regular}>{currency}</Text>
      <InputSelectControlField
        width="180px"
        data-testid={`${dataTestId}.${currency}`}
        placeholder={
          hasAccountsInCurrency
            ? 'Select a bank account'
            : `No ${currency} accounts`
        }
        emptyState={
          <Box data-testid={`${dataTestId}.${currency}.emptyState`}>
            <ControlEmptyState
              title={`No ${currency} accounts`}
              content={`${serviceName} has no bank account held in ${currency}.`}
            />
          </Box>
        }
        options={
          canClear ? [{ value: '', label: 'None' }, ...options] : options
        }
        initialValue={value[currency]}
        onChange={(bankAccountId: string) =>
          onChange({ ...value, [currency]: bankAccountId })
        }
      />
    </Flex>
  )
}

/** The service rejects a bank account that is not held in the payment's currency. */
const optionsForCurrency = (
  bankAccounts: PaymentBankAccountOption[],
  currency: Currency
) =>
  bankAccounts
    .filter(account => account.currency === currency)
    .map(account => ({ value: account.id, label: account.name ?? account.id }))
